import React from 'react'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Button,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled'
import { useNavigate } from 'react-router-dom'
import headerBgImage from '../../assets/images/arbol-ec/fondos/021.jpg'
import { MAIN } from '../../config/routes'
import { publicacionesImages, tesisData } from '../../utils/publications'
import Pagination from '@mui/material/Pagination'
import { TextField, InputAdornment } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import Articulos from './Articulos'

const ScientificWorkSection = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const [page, setPage] = React.useState(1)
  const itemsPerPage = 6
  const [searchTerm, setSearchTerm] = React.useState('')

  const filteredData = React.useMemo(() => {
    return tesisData.filter(item =>
      (item.title + ' ' + (item.authors || item.autores))
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    )
  }, [searchTerm])

  const totalPages = Math.ceil(filteredData.length / itemsPerPage)

  // Paginar los datos
  const paginatedData = filteredData.slice((page - 1) * itemsPerPage, page * itemsPerPage)

  const handleChangePage = (_, value) => {
    setPage(value)
  }

  const handleOnClick = () => navigate(MAIN)

  return (
    <Box sx={{ fontFamily: theme.typography.fontFamily }}>
      <Box
        sx={{
          backgroundImage: `url(${headerBgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'top',
          position: 'relative',
          color: theme.palette.text.ivory,
          mt: '5.8vh',
          height: '40vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        }}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: `${theme.palette.text.darkBlue}AA`,
          }}
        />
        <Box sx={{ position: 'relative', zIndex: 1, px: { xs: 4, md: 5 } }}>
          <Typography
            variant="body1"
            sx={{
              maxWidth: 700,
              color: theme.palette.text.white,
              fontWeight: 600,
              fontSize: '1.2rem',
            }}>
            Explora las investigaciones más relevantes sobre arbolado urbano, servicios
            ecosistémicos y tecnologías aplicadas.
          </Typography>
        </Box>
      </Box>

      <Box pb={4} pt={{ xs: 1, md: 2 }} px={{ xs: 5, md: 8, lg: 10 }}>
        {/* Breadcrumb */}
        <Box component="nav" mt={3}>
          <Box
            component="ol"
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              listStyle: 'none',
              justifyContent: 'end',
              p: 0,
              m: 0,
              fontSize: 16,
              fontWeight: 400,
            }}>
            <Box component="li">
              <Button
                onClick={handleOnClick}
                variant="text"
                sx={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: theme.palette.text.mustard,
                  p: 0,
                  px: 2,
                  minWidth: 0,
                  '&:hover': { color: theme.palette.text.primary },
                }}>
                Inicio
              </Button>
            </Box>
            <Box
              component="li"
              sx={{
                pl: 1,
                position: 'relative',
                fontWeight: 500,
                color: theme.palette.text.secondary,
              }}>
              <Box component="span" sx={{ pr: 1 }}>
                /
              </Box>{' '}
              Publicaciones
            </Box>
          </Box>
        </Box>

        {/* Título */}
        <Divider />
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            borderLeft: `4px solid ${theme.palette.secondary.main}`,
            pl: 4,
          }}>
          <Typography
            variant="h2"
            gutterBottom
            fontWeight={600}
            sx={{ color: theme.palette.text.forestGreen, my: 2 }}>
            Publicaciones
          </Typography>
        </Box>

        <Articulos />

        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            borderLeft: `4px solid ${theme.palette.secondary.main}`,
            pl: 2,
            mt: 8,
            mb: 4,
          }}>
          <Typography
            variant="h4"
            gutterBottom
            fontWeight={600}
            sx={{ color: theme.palette.text.forestGreen, my: 0 }}>
            Trabajo de integración curricular
          </Typography>
        </Box>

        <Box mb={3} display="flex" justifyContent="center">
          <TextField
            placeholder="Buscar por título o autor..."
            variant="outlined"
            value={searchTerm}
            onChange={e => {
              setSearchTerm(e.target.value)
              setPage(1) // reinicia a la primera página al buscar
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ width: '100%', maxWidth: 500 }}
          />
        </Box>

        <Grid container spacing={3}>
          {paginatedData.map((item, idx) => (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <Card
                component="a"
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  boxShadow: theme.shadows[3],
                  border: `1px solid ${theme.palette.background.default}`,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:hover': {
                    color: theme.palette.text.primary,
                  },
                  '&:hover img': {
                    transform: 'scale(1.04)',
                  },
                }}>
                <Box sx={{ position: 'relative', p: 4 }}>
                  <Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                        lineHeight: 1.2,
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 4, // cambia este valor si deseas 1, 3, etc.
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        height: '4.8em', // altura estimada = lineHeight * lineClamp
                        '&:hover': {
                          color: theme.palette.secondary.main,
                        },
                      }}>
                      {item.title}
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2, bgcolor: theme.palette.primary.main }} />
                  <Box
                    height={200}
                    overflow="hidden"
                    sx={{
                      border: `1px solid ${theme.palette.primary.main}`,
                    }}>
                    <CardMedia
                      component="img"
                      image={publicacionesImages[item.image]}
                      alt={item.title}
                      sx={{
                        height: '100%',
                        width: '100%',
                        objectFit: 'cover', // importante para recortar proporcionalmente
                        boxShadow:
                          '0px 2px 8px rgba(0, 0, 0, 0.25), 0px 4px 6px rgba(0, 0, 0, 0.22)',
                        transition: 'transform 0.3s ease-in-out',
                      }}
                    />
                  </Box>
                  <CardContent sx={{ px: 0, py: 0 }}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="end"
                      gap={1}
                      mb={2}
                      mt={2}>
                      <AccessTimeFilledIcon
                        sx={{ fontSize: 16, color: theme.palette.text.mutedTeal }}
                      />
                      <Typography variant="body2" color={theme.palette.text.forestGreen}>
                        {item.date}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color={theme.palette.text.secondary}>
                      {item.authors || item.autores}
                    </Typography>
                  </CardContent>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Box mt={4} display="flex" justifyContent="center">
          <Pagination
            count={totalPages}
            page={page}
            onChange={handleChangePage}
            color="primary"
            size="large"
          />
        </Box>
      </Box>
    </Box>
  )
}

export default ScientificWorkSection
